import { useMutation, useQuery } from '@apollo/client';

import {
    faChevronDown,
    faChevronUp,
    faExternalLink,
    faPencil,
    faPlus,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import {
    CREATE_BUSINESS_CENTRAL_INTEGRATION,
    DELETE_BUSINESS_CENTRAL_INTEGRATION,
    GET_BUSINESS_CENTRAL_INTEGRATIONS,
} from '../../queries.ts';
import {
    CUSTOMER_API_INTEGRATIONS_PATH,
    CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_ADD_PATH,
    CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_EDIT_PATH,
    CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH,
    CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_SETUP_DATA_PATH,
    CUSTOMER_INTEGRATION_ERP_PATH,
} from '../../constants.ts';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../core/components/Button.tsx';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import FormField from '../../../core/components/FormField.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table, { RowControlButton, RowControlIconButton } from '../../../core/components/Table.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';

interface BusinessCentralFormModalOutletContext {
    integrations: any[];
    authTokens?: any[];
    refetch: () => Promise<any>;
}

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const ExternalLinkIcon = withIcon(faExternalLink);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);

const FORM_DEFAULT_VALUES = {
    name: '',
    businessCentralUrl: '',
    orderChannelIds: [],
    orderConfirmationChannelIds: [],
};

const BusinessCentralFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                businessCentralUrl: integration.businessCentralUrl,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                orderConfirmationChannelIds: integration.orderConfirmationChannels.map((channel) => channel.id),
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH)}>
                            {t('businessCentral.form.cancelButton')}
                        </Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    rules={{
                        required: t('businessCentral.form.nameRequired'),
                    }}
                    render={({ field }) => (
                        <FormField label={t('businessCentral.form.name')} error={formErrors.name} required>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="businessCentralUrl"
                    control={form.control}
                    rules={{
                        required: t('businessCentral.form.businessCentralUrlRequired'),
                        validate: (value) => {
                            try {
                                new URL(value);
                                return true;
                            } catch (e) {
                                return t('businessCentral.form.businessCentralUrlInvalid');
                            }
                        },
                    }}
                    render={({ field }) => (
                        <FormField
                            label={t('businessCentral.form.businessCentralUrl')}
                            error={formErrors.businessCentralUrl}
                            required
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('businessCentral.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                            <CheckboxGroup
                                {...field}
                                options={
                                    orderChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="orderConfirmationChannelIds"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('businessCentral.form.orderConfirmationChannelIds')}
                            error={formErrors.orderConfirmationChannelIds}
                        >
                            <CheckboxGroup
                                {...field}
                                options={
                                    orderConfirmationChannels.map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    })) || []
                                }
                                onValueChange={(value) => field.onChange({ target: { value } })}
                            />
                        </FormField>
                    )}
                />
            </form>
        </Modal>
    );
};

export const AddBusinessCentralFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<BusinessCentralFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_BUSINESS_CENTRAL_INTEGRATION, {
        refetchQueries: [GET_BUSINESS_CENTRAL_INTEGRATIONS],
    });
    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    businessCentralUrl: formData.businessCentralUrl,
                    orderChannels: formData.orderChannelIds,
                    orderConfirmationChannels: formData.orderConfirmationChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('businessCentral.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('businessCentral.createError'), status: 'error' });
        }
    };

    return (
        <BusinessCentralFormModal
            title={t('businessCentral.form.createTitle')}
            submitButtonLabel={t('businessCentral.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH))}
        />
    );
};

const CollapsibleSection = ({
    title,
    className,
    children,
    initialIsCollapsed = true,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    title: React.ReactNode;
    initialIsCollapsed?: boolean;
}) => {
    const [isCollapsed, setIsCollapsed] = useState(initialIsCollapsed);

    return (
        <div {...props} className={classnames('flex flex-col gap-2', className)}>
            <div
                className="flex gap-2 items-center justify-between hover:bg-surface-medium px-2 py-1 rounded transition-colors cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <h3 className="font-medium text-base">{title}</h3>
                <span className="text-sm text-content-medium">
                    {isCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                </span>
            </div>
            <div className={classnames(isCollapsed ? 'hidden' : 'block')}>{children}</div>
        </div>
    );
};

const useCopyValueToClipboard = () => {
    const { t } = useTranslation('integrations');

    const { publishToast } = useToaster();

    return (value: string, targetNode: any = document) => {
        if (!value) {
            return;
        }

        navigator.clipboard
            .writeText(value)
            .then(() => {
                const keyElement = targetNode.querySelector('.js-key') as HTMLSpanElement;
                if (keyElement) {
                    window.getSelection().selectAllChildren(keyElement);
                }
            })
            .then(() => {
                publishToast({ description: t('businessCentral.setupDataModal.copySuccess'), status: 'success' });
            })
            .catch((error) => {
                publishToast({ description: t('businessCentral.setupDataModal.copyError'), status: 'error' });
            });
    };
};

const SetupDataChannel = ({ channel, initialIsCollapsed }: { channel: any; initialIsCollapsed?: boolean }) => {
    const { t } = useTranslation('integrations');

    const copyValueToClipboard = useCopyValueToClipboard();

    const rows = [
        [t('config:configPage.masterData.form.channelId'), channel.id],
        [
            t('config:configPage.masterData.form.clientLookupDefinitionId'),
            channel.masterDataConfig.clientLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.contactLookupDefinitionId'),
            channel.masterDataConfig.contactLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.emailLookupDefinitionId'),
            channel.masterDataConfig.emailLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.articleLookupDefinitionId'),
            channel.masterDataConfig.articleLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.debitorArticleLookupDefinitionId'),
            channel.masterDataConfig.debitorArticleLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.conversionFactorLookupDefinitionId'),
            channel.masterDataConfig.conversionFactorLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.frameworkContractLookupDefinitionId'),
            channel.masterDataConfig.frameworkContractLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.invoiceAddressLookupDefinitionId'),
            channel.masterDataConfig.invoiceAddressLookupDefinitionId,
        ],
        [
            t('config:configPage.masterData.form.deliveryAddressLookupDefinitionId'),
            channel.masterDataConfig.deliveryAddressLookupDefinitionId,
        ],
    ];

    return (
        <CollapsibleSection title={channel.name} initialIsCollapsed={initialIsCollapsed}>
            {rows.map(([label, value], index) => (
                <div
                    key={index}
                    className={classnames(
                        'flex gap-2 items-center justify-between px-2 py-1 rounded transition-colors w-full',
                        value && 'cursor-pointer  hover:bg-surface-medium'
                    )}
                    onClick={(e) => copyValueToClipboard(value, e.currentTarget)}
                >
                    <span className="text-sm select-none">{label}</span>
                    {value && (
                        <span className="py-1 px-2 rounded bg-surface-strong text-xs text-right select-all font-mono whitespace-nowrap js-key">
                            {value}
                        </span>
                    )}
                </div>
            ))}
        </CollapsibleSection>
    );
};

export const SetupDataBusinessCentralModal = () => {
    const { t } = useTranslation('integrations');
    const { integrations, authTokens } = useOutletContext<BusinessCentralFormModalOutletContext>();

    const navigate = useNavigate();
    const copyValueToClipboard = useCopyValueToClipboard();

    const { integrationId } = useParams();
    const integration = integrations?.find((integration) => integration.id === integrationId);

    const orderChannels = integration?.orderChannels || [];
    const orderConfirmationChannels = integration?.orderConfirmationChannels || [];

    const totalChannelCount = orderChannels.length + orderConfirmationChannels.length;

    return (
        <Modal
            className="md:min-w-[40rem] pb-8"
            visible={true}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH))}
            title={t('businessCentral.setupDataModal.title')}
        >
            <div className="flex flex-col gap-6">
                <CollapsibleSection
                    title={t('businessCentral.setupDataModal.authTokensTitle')}
                    initialIsCollapsed={false}
                >
                    <div className="flex flex-col">
                        {authTokens.map(({ name, key }, index) => (
                            <div
                                className={classnames(
                                    'flex gap-2 items-center justify-between px-2 py-1 rounded transition-colors w-full',
                                    key && 'cursor-pointer  hover:bg-surface-medium'
                                )}
                                onClick={(e) => copyValueToClipboard(key, e.currentTarget)}
                            >
                                <span className="text-sm select-none">{name}</span>

                                {key && (
                                    <span className="py-1 px-2 rounded bg-surface-strong text-xs text-right select-all font-mono whitespace-nowrap js-key">
                                        {key}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    <p className="text-sm p-2 py-3">
                        <a
                            href={url(CUSTOMER_API_INTEGRATIONS_PATH)}
                            className="text-content-brand-default hover:text-content-brand-hover"
                            target="_blank"
                        >
                            {t('businessCentral.setupDataModal.manageAuthTokens')}
                        </a>
                    </p>
                </CollapsibleSection>

                {orderChannels.map((channel) => (
                    <SetupDataChannel key={channel.id} channel={channel} initialIsCollapsed={totalChannelCount > 1} />
                ))}

                {orderConfirmationChannels.map((channel) => (
                    <SetupDataChannel key={channel.id} channel={channel} initialIsCollapsed={totalChannelCount > 1} />
                ))}
            </div>
        </Modal>
    );
};

const BusinessCentralRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_BUSINESS_CENTRAL_INTEGRATION, {
        refetchQueries: [GET_BUSINESS_CENTRAL_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('businessCentral.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('businessCentral.deleteError'), status: 'error' });
        }
    };

    const channels = integration ? [...integration.orderChannels, ...integration.orderConfirmationChannels] : [];

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name}</Table.Cell>
                <Table.Cell>
                    <div className="flex gap-1.5 flex-wrap">
                        {channels.map((channel) => (
                            <span
                                className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                                key={channel.id}
                            >
                                {channel.name}
                            </span>
                        ))}
                    </div>
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <RowControlButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_SETUP_DATA_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                {t('businessCentral.table.setupDataButton')}
                            </Link>
                        </RowControlButton>

                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_EDIT_PATH, {
                                    integrationId: integration.id,
                                })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={handleDeleteIntegration}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

const BusinessCentralIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_BUSINESS_CENTRAL_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.businessCentralIntegrations || [];
    const authTokens = data?.apiIntegrations || [];

    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')}`;

    return (
        <Layout>
            <SettingsNavigation />
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('businessCentral.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('businessCentral.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('businessCentral.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_ADD_PATH)}>
                                <AddIcon />
                                {t('businessCentral.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">
                                    {t('businessCentral.table.name')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('businessCentral.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('businessCentral.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <BusinessCentralRow
                                        key={integration.id}
                                        integration={integration}
                                        refetch={refetch}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center text-content-medium">
                                        {t('businessCentral.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, authTokens, refetch }} />
        </Layout>
    );
};

export default BusinessCentralIntegrationPage;
